// collector/prewarm.js
//
// The morning download (collector-app P2-C part B, locked answer L-F): once the
// day is on the phone, ask for the pictures the day will show, so the screens
// still have them with no signal. The requests go through the page like any
// other; the service worker's photo-host route (src/sw.js, CacheFirst, status
// 200 only) is what stores them — this file only asks, and counts.
//
// WHAT IS ASKED FOR (budget about 1 MB for 30 lines):
//   - `thumbUrl` of every photo whose thumbnail is a real thumbnail
//     (`thumbIsOriginal: false`) — the card shows these;
//   - `mediumUrl` of the FIRST photo of each line (the one the detail sheet
//     opens on), when it has one.
// A photo with no small version is NOT pulled ahead of time: its "thumbnail" is
// the full original (about 150 KB), more than all the thumbnails together; it
// loads on demand when there is signal. Only the photo host is asked: that is
// the one host the service worker caches (src/utils/cdnHost.js).
//
// HOW: four at a time, `fetch(url, { mode: 'cors' })` (an opaque response would
// be booked at ~7 MB each and never cached), skipping what the cache already
// holds — so an interrupted run is resumed by simply running again.
//
// A picture counts as downloaded only when it is IN THE CACHE afterwards, not
// when the fetch succeeded: a page still controlled by an older worker (the
// collector deferred the update prompt) or a cache write refused for quota
// fetches fine and stores nothing, and «جاهز من غير نت» must not be said then.
//
// Pure and injectable: no Vue, no store, no globals read at import time. The
// store that drives it (stores/collectorPrewarmStore.js) passes the cache
// lookup and the fetch in.
import { orderedPhotos, photoThumb } from '@/collector/lineView.js';
import { isCdnUrl } from '@/utils/cdnHost.js';

/** Requests in the air at once — a market phone's signal is not worth more. */
export const PREWARM_CONCURRENCY = 4;
/** One picture that takes longer than this is a failure, not a stuck slot. */
export const PREWARM_TIMEOUT_MS = 20_000;
/** This many attempts in a row with not one success: the host or the signal is out — stop asking. */
export const PREWARM_FAIL_FAST = 8;
/** The worker stores a picture as it streams; give it this long before calling one missing. */
export const PREWARM_VERIFY_DELAY_MS = 400;

/**
 * The URLs to download for a day, thumbnails first (the cards need them
 * first), each once. A line that moved off my day is skipped.
 *
 * @param {object|null} day - the day document as the server sent it
 * @returns {string[]}
 */
export function selectPrewarmUrls(day) {
  const thumbs = [];
  const mediums = [];
  for (const shop of day?.shops ?? []) {
    for (const line of shop?.lines ?? []) {
      if (line?.movedAway) continue;
      const photos = orderedPhotos(line);
      for (const photo of photos) {
        if (photo.thumbIsOriginal === false) thumbs.push(photoThumb(photo));
      }
      const [first] = photos;
      if (first?.mediumUrl) mediums.push(first.mediumUrl);
    }
  }
  return [...new Set([...thumbs, ...mediums])].filter((url) => isCdnUrl(url));
}

/**
 * The most pictures the morning download asks for before it stops pulling the
 * medium size of the plans that are not on screen. The worker's photo cache
 * holds 1,000 pictures for 7 days (src/sw.js): this, the offline preview's ≤600
 * (PREVIEW_PHOTO_MAX), and a little room for what the cards ask for on their own.
 */
export const PREWARM_URL_BUDGET = 380;

/**
 * ONE document for the download of every open plan kept on the phone
 * (collector-offline P3-A): the active plan's shops first, then the shops of
 * the other plans. `selectPrewarmUrls` reads only `shops[].lines[]`, so this
 * document needs nothing else of a plan. With no other plan it is the active
 * document itself — one plan alone is downloaded exactly as before.
 *
 * Over the budget, the OTHER plans lose their medium size (their cards still
 * get their thumbnails; the big picture of the detail sheet loads on demand
 * when there is signal), and `trimmed` says so. The active plan keeps all.
 * Nothing passed in is changed.
 *
 * @param {object|null} active - the document of the plan on screen
 * @param {Array<object|null>} [others] - the documents of the other open plans
 * @returns {{ day: object|null, trimmed: boolean }}
 */
export function unionPrewarmDay(active, others = [], { budget = PREWARM_URL_BUDGET } = {}) {
  const rest = others.filter((doc) => doc && Array.isArray(doc.shops));
  if (!active || rest.length === 0) return { day: active, trimmed: false };
  const merge = (docs) => ({ ...active, shops: [active, ...docs].flatMap((doc) => doc.shops ?? []) });
  const whole = merge(rest);
  if (selectPrewarmUrls(whole).length <= budget) return { day: whole, trimmed: false };
  return { day: merge(rest.map(withoutMediums)), trimmed: true };
}

function withoutMediums(doc) {
  const thumbsOnly = (photo) => (photo?.mediumUrl ? { ...photo, mediumUrl: null } : photo);
  return {
    ...doc,
    shops: doc.shops.map((shop) => ({
      ...shop,
      lines: (shop?.lines ?? []).map((line) => ({
        ...line,
        photos: Array.isArray(line?.photos) ? line.photos.map(thumbsOnly) : line?.photos,
      })),
    })),
  };
}

/**
 * One picture, asked with CORS and read to the end (the cache copy is made as it streams).
 * `onBytes` hears its size: the host's `Content-Length`, else what was read.
 */
export async function fetchPhoto(url, { fetchImpl = globalThis.fetch, timeoutMs = PREWARM_TIMEOUT_MS, onBytes } = {}) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const response = await fetchImpl(url, { mode: 'cors', credentials: 'omit', signal: controller?.signal });
    if (!response?.ok) return false;
    const buffer = await response.arrayBuffer();
    onBytes?.(Number(response.headers?.get?.('content-length')) || buffer?.byteLength || 0);
    return true;
  } catch {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Download what is not on the phone yet.
 *
 * @param {string[]} urls
 * @param {object} io
 * @param {(url: string) => Promise<boolean>} io.has   already in the cache?
 * @param {(url: string) => Promise<boolean>} io.load  fetch it; true when it came
 * @param {(progress: {done: number, total: number}) => void} [io.onProgress]
 * @param {() => boolean} [io.shouldStop]   checked before every request (signal gone, page left)
 * @param {number} [io.concurrency]
 * @param {number} [io.failFast]
 * @param {number} [io.verifyDelayMs]
 * @returns {Promise<{ total: number, done: number, failed: number, stopped: boolean }>}
 *   `done` counts the pictures on the phone (cached before, or fetched now AND
 *   found in the cache afterwards); `failed` = asked for and not there;
 *   `stopped` = the run ended early (offline, or nothing but failures).
 */
export async function runPrewarm(
  urls,
  {
    has,
    load,
    onProgress = () => {},
    shouldStop = () => false,
    concurrency = PREWARM_CONCURRENCY,
    failFast = PREWARM_FAIL_FAST,
    verifyDelayMs = PREWARM_VERIFY_DELAY_MS,
  },
) {
  const total = urls.length;
  const cached = await Promise.all(urls.map((url) => has(url).catch(() => false)));
  const todo = urls.filter((_url, index) => !cached[index]);
  let done = total - todo.length;
  let failed = 0;
  let succeeded = 0;
  const fetched = [];
  let stopped = false;
  let next = 0;
  onProgress({ done, total });

  async function worker() {
    while (!stopped) {
      if (shouldStop()) {
        stopped = true;
        return;
      }
      const url = todo[next];
      next += 1;
      if (url === undefined) return;
      const came = await load(url);
      if (came) {
        done += 1;
        succeeded += 1;
        fetched.push(url);
        onProgress({ done, total });
      } else {
        failed += 1;
        // Nothing has ever come, and the first attempts all failed: the host
        // does not answer (or answers without the permission header). Do not
        // spend 180 requests finding that out.
        if (succeeded === 0 && failed >= failFast) stopped = true;
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrency, todo.length) }, worker));

  // Did the worker really keep them? One look, and one more after a moment.
  let missing = await notCached(fetched, has);
  if (missing.length) {
    await new Promise((resolve) => {
      setTimeout(resolve, verifyDelayMs);
    });
    missing = await notCached(missing, has);
  }
  if (missing.length) {
    done -= missing.length;
    failed += missing.length;
    // Fetched fine and kept nothing: asking again now would only repeat it.
    stopped = true;
  }
  return { total, done, failed, stopped };
}

async function notCached(urls, has) {
  const found = await Promise.all(urls.map((url) => has(url).catch(() => false)));
  return urls.filter((_url, index) => !found[index]);
}

// ── The offline supplier preview's photos (supplier-preview-offline L-N) ─────

/** One pass asks for at most this many pictures… (owner decision 20 Oct: a good show over saving data) */
export const PREVIEW_PHOTO_MAX = 600;
/** …and stops once this much came down, whichever is first — on any connection. */
export const PREVIEW_PHOTO_MAX_BYTES = 100 * 1024 * 1024;

function parseBody(body) {
  if (typeof body !== 'string') return body ?? null;
  try {
    return JSON.parse(body);
  } catch {
    return null;
  }
}

/** The saved answers of one copy whose key starts with `GET <path>` (the query follows a `?`). */
function bodiesOf(row, path) {
  return Object.entries(row?.responses ?? {})
    .filter(([key]) => key === `GET ${path}` || key.startsWith(`GET ${path}?`))
    .map(([, body]) => parseBody(body))
    .filter(Boolean);
}

const attrs = (resource) => resource?.attributes ?? {};
const firstPhoto = (a) => {
  const gallery = Array.isArray(a.catalogPhotos) ? a.catalogPhotos : [];
  const first = gallery.map((p) => (typeof p === 'string' ? p : (p?.url ?? p?.src ?? p?.href ?? null))).find(Boolean);
  return first ?? a.photoUrl ?? null;
};

/** A feed page's card pictures: each post's first medium (thumbnail + the 320 px tile) and the channel avatars. */
function feedPhotos(doc) {
  const included = Array.isArray(doc?.included) ? doc.included : [];
  const find = (ref) => included.find((r) => r?.type === ref?.type && String(r?.id) === String(ref?.id));
  const urls = [];
  for (const post of Array.isArray(doc?.data) ? doc.data : []) {
    const [ref] = post?.relationships?.media?.data ?? [];
    const media = attrs(find(ref));
    urls.push(media.urls?.thumbnail ?? media.thumbnail, media.urls?.small ?? media.url);
  }
  for (const channel of included.filter((r) => r?.type === 'channels')) urls.push(attrs(channel).avatarUrl);
  return urls;
}

/**
 * The pictures the saved copies' first screens show, in the owner's order
 * (L-N), each once, at most 600: (1) the club-wide wall and «ناقص» first-page
 * cards; (2) round-robin over the shops, the first photo of each item of each
 * saved day, most recent days first; (3) Poola: each saved offer's product
 * photo and shop avatar; (4) «السوق»: each post's thumbnail and 320 px tile on
 * the seller feed and on each shop's «اهتمام السيلرز» page, plus the channel
 * avatars. Photos opened only on a tap (a post's gallery, the source-post
 * sheet) are not asked for. Only the photo host's — the one the worker keeps.
 *
 * @param {Array<object>} rows - the copies, shops in route order (day shops first)
 * @returns {{ urls: string[], byShop: Record<string, string[]> }} `byShop`: the shop's own share
 */
export function selectPreviewPhotoUrls(rows, { max = PREVIEW_PHOTO_MAX } = {}) {
  const picked = [];
  const owner = new Map();
  const take = (url, shopId) => {
    if (typeof url !== 'string' || !isCdnUrl(url) || owner.has(url)) return;
    owner.set(url, shopId);
    picked.push(url);
  };
  const each = (fn) => rows.forEach((row) => fn(row, String(row.shopId)));

  each((row, id) => {
    for (const doc of [...bodiesOf(row, '/supplier-wall-posts'), ...bodiesOf(row, '/supplier-missing-products')]) {
      for (const item of doc.data ?? []) {
        take(firstPhoto(attrs(item)), id);
        take(attrs(item).channelAvatarUrl, id);
      }
    }
  });

  // A day's detail answers were saved most recent first (the order «أيامك» shows them).
  const days = rows.map((row) =>
    Object.entries(row?.responses ?? {})
      .filter(([key]) => /^GET \/supplier-boxes\/[^/?]+(\?|$)/.test(key))
      .flatMap(([, body]) =>
        (parseBody(body)?.included ?? []).filter((r) => r?.type === 'supplier-box-items').map((item) => firstPhoto(attrs(item))),
      ),
  );
  for (let index = 0; days.some((list) => index < list.length); index += 1) {
    days.forEach((list, shop) => take(list[index], String(rows[shop].shopId)));
  }

  each((row, id) => {
    for (const doc of bodiesOf(row, '/poola-offers')) {
      for (const offer of doc.data ?? []) {
        take(attrs(offer).photoUrl, id);
        take(attrs(offer).supplierAvatarUrl, id);
      }
    }
  });
  each((row, id) => {
    for (const doc of [...bodiesOf(row, '/supplier-seller-feed-posts'), ...bodiesOf(row, '/supplier-interest-posts')]) {
      feedPhotos(doc).forEach((url) => take(url, id));
    }
  });

  const urls = picked.slice(0, max);
  const byShop = {};
  for (const url of urls) (byShop[owner.get(url)] ??= []).push(url);
  return { urls, byShop };
}
